import { AdminUser } from '@/contracts/adminUsers';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Crown, Shield, Mail } from 'lucide-react';

type Props = {
  users: AdminUser[];
  isLoading: boolean;
  onTogglePro: (userId: string, currentStatus: boolean) => void;
  onToggleRole: (userId: string, role: 'admin' | 'editor', hasRole: boolean) => void;
  onSendPasswordReset: (email: string) => void;
};

export function UsersTable({
  users,
  isLoading,
  onTogglePro,
  onToggleRole,
  onSendPasswordReset,
}: Props) {
  if (isLoading) {
    return (
      <div className="border rounded-md p-8 text-center text-muted-foreground">
        Loading users…
      </div>
    );
  }

  if (!isLoading && users.length === 0) {
    return (
      <div className="border rounded-md p-8 text-center text-muted-foreground">
        No users found
      </div>
    );
  }

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Pro</TableHead>
            <TableHead>Admin</TableHead>
            <TableHead>Editor</TableHead>
            <TableHead>Vendor</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map(user => {
            const hasAdmin = user.roles.includes('admin');
            const hasEditor = user.roles.includes('editor');

            return (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.email ?? '—'}</TableCell>
                <TableCell>{user.full_name ?? '—'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {user.is_pro_member && (
                      <Badge variant="default" className="gap-1">
                        <Crown className="h-3 w-3" />
                        Pro
                      </Badge>
                    )}
                    <Switch
                      checked={user.is_pro_member}
                      onCheckedChange={() => onTogglePro(user.user_id, user.is_pro_member)}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {hasAdmin && <Shield className="h-4 w-4 text-destructive" />}
                    <Switch
                      checked={hasAdmin}
                      onCheckedChange={() => onToggleRole(user.user_id, 'admin', hasAdmin)}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {hasEditor && <Shield className="h-4 w-4" />}
                    <Switch
                      checked={hasEditor}
                      onCheckedChange={() => onToggleRole(user.user_id, 'editor', hasEditor)}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  {user.is_vendor ? (
                    <Badge variant="secondary">Yes</Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">No</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => user.email && onSendPasswordReset(user.email)}
                    disabled={!user.email}
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
